import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Container } from '@mui/material';
import Navigation from "./header";
import { models, service, factories } from 'powerbi-client';

import { IEmbeddedReportInfo, IReportRegistration } from '../types/report';

import { ErrorProvider } from '../classes/errorProvider';
import { useListContext } from '../contexts/listContext';
import { ReportService } from '../services/reportService';
import { UtilityService } from '../services/utilityService';

import Spinner from './spinner';
import WorkingModal, { IWorkingModalProps } from './workingModal';

import '../styles/powerBiReportViewer.scss'

interface IPowerBiReportViewerProps {
	/** The PowerBI ID (GUID) or name (string) of the report. */
	reportId?: string;

	/** The PowerBI ID (GUID) of the workspace. Optional. Defaults to PowerBiDefaultWorkspaceId in AppSettings. */
	workspaceId?: string;

	/** Parameters to pass to the report in the embedded URL. Optional. */
	parameters?: Record<string, string>;

	/** Disable when adding report to pages with existing content. Optional. Defaults to true. */
	showNav?: boolean;

	/** Whether the ID represents a report or a dashboard. Dashboards are dynamic and bypass report registration checks and permissions but don't allow access to registered reports. */
	type?: "report" | "dashboard";

	/** For reports or dashboards that use row level security we need to notify the token generation process **/
	useRowLevelSecurity?: boolean;
}

const PowerBiReportViewer: React.FunctionComponent<IPowerBiReportViewerProps> = ({ reportId, workspaceId, parameters = undefined, showNav = true, type = "report", useRowLevelSecurity = false }) => {
	// Accept reportId and workspaceId as props or URL parameters
	const { reportId: paramReportId, workspaceId: paramWorkspaceId } = useParams<{ reportId?: string, workspaceId?: string }>();
	reportId ??= paramReportId;
	workspaceId ??= paramWorkspaceId;

	const listContext = useListContext();
	const [queryParameters] = useSearchParams();
	const [reportRegistration, setReportRegistration] = useState<IReportRegistration | undefined>(undefined);
	const [embeddedReportInfo, setEmbeddedReportInfo] = useState<IEmbeddedReportInfo | undefined>(undefined);
	const [isLoadingReport, setIsLoadingReport] = useState<boolean>(true);
	const [errorMessages, setErrorMessages] = useState<string[] | null>(null);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);	
	const [initMessage, setInitMessage] = useState<string | undefined>(undefined);

	const reportContainerRef = useRef<HTMLDivElement>(null);
	const powerbi = useRef<service.Service>(new service.Service(
		factories.hpmFactory,
		factories.wpmpFactory,
		factories.routerFactory
	));

	// Buffer and compare the parameters because sometimes external UI updates make the component think some of its parameters have changed when they haven't
	const [reportIdBuffer, setReportIdBuffer] = useState<string | undefined>(undefined);
	const [workspaceIdBuffer, setWorkspaceIdBuffer] = useState<string | undefined>(undefined);
	const [parametersBuffer, setParametersBuffer] = useState<Record<string, string> | undefined>(undefined); // Used to detect prop parameter changes
	const [combinedParametersBuffer, setCombinedParametersBuffer] = useState<Record<string, string> | undefined>(undefined); // Used to build the report

	// Combine path and query parameters if no parameters were passed in as props
	useEffect(() => {		
		if (parameters === undefined && queryParameters.size > 0) {
			setCombinedParametersBuffer(MergeQueryParametersToRecord(parameters));
		}
	}, []);

	// Buffer reportId
	useEffect(() => {
		if (reportId != reportIdBuffer) {
			setReportIdBuffer(reportId);
		}
	}, [reportId]);

	// Buffer workspaceId
	useEffect(() => {
		if (workspaceId != workspaceIdBuffer) {
			setWorkspaceIdBuffer(workspaceId);
		}
	}, [workspaceId]);

	// Buffer prop parameters and merge them with query parameters
	useEffect(() => {
		if (!UtilityService.areRecordContentsIdentical(parameters, parametersBuffer)) {
			setParametersBuffer(parameters);
			setCombinedParametersBuffer(MergeQueryParametersToRecord(parameters));
		}
	}, [parameters]);

	// Regerate report if any of the key inputs change
	useEffect(() => {
		if (reportIdBuffer == undefined) {
			setErrorMessages(['"reportId" is required. It can be passed in as a property or as a URL parameter.']);
			setIsLoadingReport(false);
			return;
		}

		setReportRegistration(listContext.reportRegistrations.find(r => r.reportId.toLowerCase() === reportIdBuffer?.toLowerCase() || r.name.toLowerCase() === reportIdBuffer?.toLowerCase()));		
		getReport();
	}, [reportIdBuffer, workspaceIdBuffer, combinedParametersBuffer]);

	// Hide our loading indicators after receiving the report information and let PowerBI's take over
	useEffect(() => {
		if (embeddedReportInfo) {
			setIsLoadingReport(false);
			displayEmbeddedReport();
		}
	}, [embeddedReportInfo]);

	// After getting our report registration record, populate the display name of the report in the message shared by the modal and spinner
	useEffect(() => {
		setInitMessage(`Initializing ${reportRegistration?.displayName ?? "report"}...`);
	}, [reportRegistration])

	// Show the modal for the loading message if the navigation is visible (report is full screen)
	useEffect(() => {
		if (showNav) {
			setWorkingModalParams({ params: { content: initMessage } });
		}
	}, [initMessage])

	useEffect(() => {
		if (!isLoadingReport) {
			setWorkingModalParams(undefined);
		}
	}, [isLoadingReport])

	const getReport = async () => {
		setIsLoadingReport(true);
		setEmbeddedReportInfo(undefined);
		setErrorMessages(undefined);

		ReportService.getEmbeddedPowerBiReport(reportIdBuffer, workspaceIdBuffer, type, useRowLevelSecurity)
			.then(response => {
				console.log('ReportService.getEmbeddedPowerBiReport: SUCCESS', response)
				setEmbeddedReportInfo(response.data as IEmbeddedReportInfo);
			})
			.catch(error => {
				console.log('ReportService.getEmbeddedPowerBiReport: ERROR', error)
				setErrorMessages([ErrorProvider.getError(error)]);
				setIsLoadingReport(false);
			});
	}

	// Add query parameters to the generated embedUrl if this report requires them
	const AppendQueryParametersToUrl = (baseUrl: string, parameters?: Record<string, string>): string => {
		if (!parameters || Object.keys(parameters).length === 0) return baseUrl;

		const queryString = Object.entries(parameters)
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
			.join('&');
		const joiner = baseUrl.includes('?') ? '&' : '?';
		return `${baseUrl}${joiner}${queryString}`;
	}

	// Merge the parameters passed in as a property and via the query string
	const MergeQueryParametersToRecord = (record?: Record<string, string>, overwriteDuplicates: boolean = true): Record<string, string> => {
		if (queryParameters?.size === 0) return record;
		if (!record) record = {};
		queryParameters.forEach((value, key) => {
			if (overwriteDuplicates || !(key in record)) {
				record[key] = value;
			}
		});
		return record;
	}

	// Embed the Power BI report in the container
	const displayEmbeddedReport = async () => {
		if (!reportContainerRef.current || !embeddedReportInfo) return;

		// Reset the container before possibly embedding another report
		if (powerbi.current) powerbi.current.reset(reportContainerRef.current);

		const config: models.IReportEmbedConfiguration = {
			type: 'report',
			tokenType: models.TokenType.Embed,
			accessToken: embeddedReportInfo.token,
			embedUrl: AppendQueryParametersToUrl(embeddedReportInfo.embedUrl, combinedParametersBuffer),
			id: embeddedReportInfo.id,
			permissions: models.Permissions.Read,
			settings: {
				panes: {
					filters: {
						expanded: false,
						visible: true
					},
					pageNavigation: {
						visible: true
					}
				},
				background: models.BackgroundType.Transparent,
				layoutType: models.LayoutType.Custom,
				customLayout: {
					displayOption: models.DisplayOption.FitToWidth
				}
			}
		};

		powerbi.current.embed(
			reportContainerRef.current,
			config
		);
	}

	return (
		<>
			{showNav && <Navigation></Navigation>}
			<Container maxWidth="lg" className="route-container">
				<section className='hwi-power-bi-report-viewer'>
					<Spinner dependency={!showNav && isLoadingReport} text={initMessage}></Spinner>
					{embeddedReportInfo?.displayName && <h1 className="report-header">{embeddedReportInfo.displayName}</h1>}
					{errorMessages &&
						<Alert className="alert-error"
							severity="error">
							{(errorMessages?.length ?? 0) > 0 &&
								<ul className={errorMessages.length == 1 ? 'single' : ''}>
									{errorMessages.map((msg, index) => (
										<li key={index}>{msg}</li>
									))}
								</ul>
							}
						</Alert>
					}
					<div id="reportContainer" className={isLoadingReport || errorMessages ? 'hidden' : ''} ref={reportContainerRef}></div>
				</section>
				<WorkingModal params={workingModalParams?.params}></WorkingModal>
			</Container>
		</>
	)
}

export default PowerBiReportViewer;